import { useState } from "react";
import { toast } from "sonner";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { api, errMsg } from "@/lib/api";
import { PageTitle, Panel, Btn, Sel, NameFields, emptyName, toNamePayload, StatusBadge } from "@/admin/ui";

const groupFeatures = (fs) => fs.reduce((acc, f) => ({ ...acc, [f.feature_type]: [...(acc[f.feature_type] || []), f] }), {});

function PanelA({ a }) {
  const g = groupFeatures(a.features);
  return (
    <div className="grid gap-4 lg:grid-cols-2" data-testid="panel-a">
      <Panel title="Components">
        {a.components.map((c, i) => <p key={i} className="text-sm"><span className="text-muted-foreground">{c.name_part} {c.part_index + 1}:</span> <strong>{c.text}</strong> <span className="font-mono-ui text-xs text-primary">{c.type}{c.initials ? ` [${c.initials.join(" ")}]` : ""}</span></p>)}
        <p className="mt-3 text-xs text-muted-foreground">Structures: <span className="font-mono-ui text-foreground" data-testid="qc-structures">{a.structures.join(", ")}</span></p>
        {a.limitations.map((l, i) => <p key={i} className="mt-2 border-l-2 border-amber-400 pl-2 text-xs text-amber-200">{l}</p>)}
      </Panel>
      <Panel title="Letter features">
        <div className="space-y-2 text-xs">
          {Object.entries(g).filter(([k]) => k !== "letter").map(([k, fs]) => (
            <p key={k}><span className="inline-block w-36 font-mono-ui text-muted-foreground">{k}</span>{fs.map((f, i) => <span key={i} className="mr-2 rounded bg-secondary px-1.5 py-0.5 font-mono-ui">{f.value}{f.name_part !== "any" ? <sub className="ml-0.5 text-muted-foreground">{f.name_part[0]}</sub> : ""}{f.count ? `×${f.count}` : ""}</span>)}</p>
          ))}
        </div>
      </Panel>
    </div>
  );
}

const PanelB = ({ r }) => (
  <div className="overflow-x-auto rounded-md border border-border" data-testid="panel-b">
    <table className="w-full text-sm">
      <thead className="bg-secondary/50 text-left text-xs uppercase text-muted-foreground"><tr>{["Rule", "Feature", "Matched", "Priority", "Domain", "Status"].map((h) => <th key={h} className="px-3 py-2">{h}</th>)}</tr></thead>
      <tbody>
        {r.findings.map((f) => (
          <tr key={f.rule_id} className="border-t border-border">
            <td className="px-3 py-2 font-mono-ui text-xs">{f.code || f.rule_id.slice(0, 8)}</td>
            <td className="px-3 py-2">{f.feature_type} <span className="font-mono-ui">{f.target}</span></td>
            <td className="px-3 py-2 text-xs text-muted-foreground">{f.evidence.map((e) => `${e.name_part}:${e.word || e.value}`).join(", ")}</td>
            <td className="px-3 py-2">{f.priority}</td>
            <td className="px-3 py-2 text-xs">{f.domain}</td>
            <td className="px-3 py-2"><StatusBadge s={f.status} /> <span className="text-xs text-muted-foreground">{f.status_reason}</span></td>
          </tr>
        ))}
        {!r.findings.length && <tr><td colSpan={6} className="px-3 py-8 text-center text-muted-foreground">No approved rules match this name in the selected ruleset.</td></tr>}
      </tbody>
    </table>
  </div>
);

const PanelC = ({ r }) => (
  <div className="grid gap-4 lg:grid-cols-2" data-testid="panel-c">
    <Panel title="Blocking issues">{r.blocking_issues.length ? r.blocking_issues.map((b, i) => <p key={i} className="text-sm text-red-400">{b}</p>) : <p className="text-sm text-emerald-400">None</p>}</Panel>
    <Panel title="Reinforcing themes (D)">{r.reinforcing_themes.length ? r.reinforcing_themes.map((t) => <p key={t.theme} className="text-sm">{t.theme} <span className="text-muted-foreground">× {t.count}</span></p>) : <p className="text-sm text-muted-foreground">None</p>}</Panel>
    <Panel title="Applied reconciliation groups">{r.overlaps.length ? r.overlaps.map((o) => <p key={o.group_id} className="text-sm">{o.type}: {o.rule_ids.length} rules {o.unresolved && <span className="text-red-400">(unresolved)</span>}</p>) : <p className="text-sm text-muted-foreground">None</p>}</Panel>
    <Panel title="Possible overlaps not yet reviewed">{r.unreviewed_overlaps.length ? r.unreviewed_overlaps.map((o, i) => <p key={i} className="text-sm text-amber-300">{o.reason}</p>) : <p className="text-sm text-muted-foreground">None</p>}</Panel>
  </div>
);

const PanelD = ({ a, r }) => (
  <div className="rounded-md bg-[#FDFBF7] p-10 text-stone-900" data-testid="panel-d">
    <p className="font-mono-ui text-[10px] uppercase tracking-[0.25em] text-amber-800">Structured draft · AI narrative composition arrives in Stage 3</p>
    <h2 className="mt-4 font-serif-display text-4xl">{a.display_name}</h2>
    <p className="font-serif-display italic text-stone-600">Your Name Has More to Say Than You Think.</p>
    {!r.sections.length && <p className="mt-6 text-sm text-stone-500">No approved findings to compose yet.</p>}
    {r.sections.map((s) => (
      <div key={s.domain} className="mt-8 border-t border-stone-300 pt-4">
        <h3 className="font-serif-display text-2xl">{s.title}</h3>
        {!!s.positives.length && <ul className="mt-2 list-disc pl-5 text-sm">{s.positives.map((p, i) => <li key={i}>{p}</li>)}</ul>}
        {!!s.challenges.length && <ul className="mt-2 list-disc pl-5 text-sm text-stone-600">{s.challenges.map((p, i) => <li key={i}>Challenge: {p}</li>)}</ul>}
      </div>
    ))}
  </div>
);

const PanelE = ({ r }) => (
  <div className="space-y-3" data-testid="panel-e">
    {r.sections.map((s) => (
      <Panel key={s.domain} title={s.title}>
        {r.findings.filter((f) => s.rule_ids.includes(f.rule_id)).map((f) => (
          <p key={f.rule_id} className="text-xs"><span className="font-mono-ui">{f.code || f.rule_id.slice(0, 8)}</span> ← {f.source?.document_title || "unknown source"} {f.source?.section && `› ${f.source.section}`} {f.source?.passage_ids?.length ? <span className="text-muted-foreground">[{f.source.passage_ids.length} passage(s)]</span> : ""}</p>
        ))}
      </Panel>
    ))}
    {!r.sections.length && <p className="text-sm text-muted-foreground">Nothing to audit.</p>}
  </div>
);

export default function Analyzer() {
  const [n, setN] = useState(emptyName);
  const [ruleset, setRuleset] = useState("draft");
  const [res, setRes] = useState(null);
  const run = async () => {
    try { setRes((await api.post("/admin/analyze", { ...toNamePayload(n), ruleset })).data); } catch (e) { toast.error(errMsg(e)); }
  };
  const saveGolden = async () => {
    try {
      await api.post("/admin/golden", { label: res.analysis.display_name, input: toNamePayload(n), expected_rule_ids: res.result.selected_rule_ids, expected_categories: res.result.categories, expected_structures: res.analysis.structures, notes: `Captured from QC (${res.result.ruleset})` });
      toast.success("Saved as golden test case");
    } catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <div>
      <PageTitle title="Quality Control" sub="Analyse any name against the draft (approved, unpublished) or the active published ruleset." />
      <Panel>
        <NameFields n={n} setN={setN} />
        <div className="mt-3 flex flex-wrap gap-2">
          <Sel value={ruleset} onChange={setRuleset} options={[["draft", "Draft (approved rules)"], ["active", "Active published version"]]} testId="qc-ruleset-select" />
          <Btn onClick={run} disabled={!n.first.trim()} data-testid="qc-analyze-btn">Analyse</Btn>
          {res && <Btn variant="ghost" onClick={saveGolden} data-testid="qc-save-golden-btn">Save as golden test</Btn>}
        </div>
      </Panel>
      {res && (
        <div className="mt-6">
          <p className="mb-3 text-xs text-muted-foreground" data-testid="qc-summary">Ruleset {res.result.ruleset} · {res.rules_in_ruleset} rules · fingerprint <span className="font-mono-ui">{res.result.fingerprint}</span> · {res.result.releasable ? <span className="text-emerald-400">releasable</span> : <span className="text-amber-300">not releasable</span>}</p>
          <Tabs defaultValue="a">
            <TabsList className="bg-secondary">
              {[["a", "A · Name analysis"], ["b", "B · Interpretations"], ["c", "C · Overlaps"], ["d", "D · Report preview"], ["e", "E · Source audit"]].map(([v, l]) => <TabsTrigger key={v} value={v} data-testid={`qc-tab-${v}`}>{l}</TabsTrigger>)}
            </TabsList>
            <TabsContent value="a"><PanelA a={res.analysis} /></TabsContent>
            <TabsContent value="b"><PanelB r={res.result} /></TabsContent>
            <TabsContent value="c"><PanelC r={res.result} /></TabsContent>
            <TabsContent value="d"><PanelD a={res.analysis} r={res.result} /></TabsContent>
            <TabsContent value="e"><PanelE r={res.result} /></TabsContent>
          </Tabs>
        </div>
      )}
    </div>
  );
}
